/**
 * StatusBadge component for displaying essay status with appropriate styling.
 * 
 * Provides consistent status display across the application:
 * - Color-coded badges for each status
 * - Human-readable labels
 * - Optional size variants
 * 
 * Called by:
 * - EssayCard.tsx for essay list
 * - EssayDetailPage.tsx for essay header
 * - VersionHistory.tsx for version status display
 */
import { Tag } from 'antd';
import type { EssayStatus } from '@compendium/shared';
import { STATUS_LABELS } from '@compendium/shared';

// =============================================================================
// Status Color Configuration
// =============================================================================

/**
 * Map essay status to Ant Design Tag color.
 * Colors are chosen to convey meaning:
 * - draft: Gray (neutral, work in progress)
 * - in_review: Blue (processing, awaiting action)
 * - revision: Orange (warning, needs attention)
 * - approved: Green (success, ready)
 * - published: Purple (special, live)
 * - archived: Dark gray (inactive)
 */
const STATUS_COLORS: Record<EssayStatus, string> = {
  draft: 'default',
  in_review: 'processing',
  revision: 'warning',
  approved: 'success',
  published: 'gold',
  archived: 'default',
};

/**
 * Additional styling for archived status to differentiate from draft.
 */
const STATUS_STYLES: Partial<Record<EssayStatus, React.CSSProperties>> = {
  archived: {
    opacity: 0.7,
    fontStyle: 'italic',
  },
};

// =============================================================================
// Component Props
// =============================================================================

interface StatusBadgeProps {
  /** The essay status to display */
  status: EssayStatus;
  /** Optional size variant */
  size?: 'small' | 'default';
  /** Optional additional styles */
  style?: React.CSSProperties;
}

// =============================================================================
// Component
// =============================================================================

/**
 * StatusBadge component.
 * Displays an essay status as a color-coded tag.
 */
function StatusBadge({ status, size, style }: StatusBadgeProps) {
  const color = STATUS_COLORS[status];
  const label = STATUS_LABELS[status];
  const additionalStyle = STATUS_STYLES[status];
  
  return (
    <Tag
      color={color}
      style={{
        ...additionalStyle,
        ...style,
        // Smaller font for small variant
        ...(size === 'small' ? { fontSize: '11px', padding: '0 4px' } : {}),
      }}
    >
      {label}
    </Tag>
  );
}

export default StatusBadge;
